import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, FileText, X, Download, FileDown, Loader2 } from 'lucide-react';
import { useGetScraperQueueUrlByDocumentIdMutation, useProxyDocumentUrlMutation } from '../../common/backend-api/backend-api';
import { getLanguageDirection } from '../../i18n/config';
import LegalDocumentFormatter from '../message/LegalDocumentFormatter';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface DocumentCardProps {
  documentId: string;
  documentTitle: string;
  documentSubtitle?: string;
  knowledgebaseName?: string;
  jurisdiction?: string;
  relevance: string;
  contentType: string;
  score: number;
  confidence?: number;
  reasoning?: string;
  keyTerms?: string[];
  showScore?: boolean;
  isUserUpload?: boolean;
  documentUrl?: string;
}

/**
 * Helper functions for parsing metadata
 */
const parseRelevance = (relevance: string, t: any): string => {
  const mapping: Record<string, string> = {
    'highly_relevant': t('components.documentSearch.relevance.highly_relevant', 'Highly Relevant'),
    'moderately_relevant': t('components.documentSearch.relevance.moderately_relevant', 'Moderately Relevant'),
    'slightly_relevant': t('components.documentSearch.relevance.slightly_relevant', 'Slightly Relevant'),
    'not_relevant': t('components.documentSearch.relevance.not_relevant', 'Not Relevant')
  };
  return mapping[relevance] || relevance;
};

const parseContentType = (contentType: string, t: any): string => {
  const mapping: Record<string, string> = {
    'case_law': t('components.documentSearch.contentType.case_law', 'Case Law'),
    'statute': t('components.documentSearch.contentType.statute', 'Statute'),
    'regulation': t('components.documentSearch.contentType.regulation', 'Regulation'),
    'legal_analysis': t('components.documentSearch.contentType.legal_analysis', 'Legal Analysis'),
    'procedural': t('components.documentSearch.contentType.procedural', 'Procedural'),
    'other': t('components.documentSearch.contentType.other', 'Other')
  };
  return mapping[contentType] || contentType;
};

const getRelevanceColor = (relevance: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
  const colorMap: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
    'highly_relevant': 'default',
    'moderately_relevant': 'secondary',
    'slightly_relevant': 'outline',
    'not_relevant': 'destructive'
  };
  return colorMap[relevance] || 'outline';
};

/**
 * Format legal document text using dedicated legal document formatter
 */
const formatLegalText = (text: string): React.ReactElement => {
  return <LegalDocumentFormatter content={text} />;
};

/**
 * Reusable document card component for displaying legal documents with metadata
 * Shows document info, metadata chips, and handles click-to-view/open behavior
 */
const RelevantDocumentCard: React.FC<DocumentCardProps> = ({
  documentId,
  documentTitle,
  documentSubtitle,
  knowledgebaseName,
  jurisdiction,
  relevance,
  contentType,
  score,
  confidence,
  reasoning,
  keyTerms,
  showScore = false,
  isUserUpload = false,
  documentUrl
}) => {
  const { t, i18n } = useTranslation();
  const [fullText, setFullText] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);



  // Mutation for fetching scraper queue URL
  const [getScraperQueueUrl] = useGetScraperQueueUrlByDocumentIdMutation();

  // Mutation for proxying document URL
  const [proxyDocumentUrl] = useProxyDocumentUrlMutation();

  const isRTL = getLanguageDirection(i18n.language) === 'rtl';

  const handleCardClick = async () => {
    // If document URL exists, open it in new tab
    if (documentId) {
      try {
        const response = await getScraperQueueUrl({ documentId }).unwrap();
        const scraperQueueURL = response.result?.url;
        if (scraperQueueURL) {
          window.open(scraperQueueURL, '_blank', 'noopener,noreferrer');
        } else {
          // Fallback: open dialog and fetch full text
          handleOpenFullText();
        }
      } catch (error) {
        console.error('Failed to fetch scraper queue URL:', error);
        // Fallback: open dialog and fetch full text
        handleOpenFullText();
      }
    } else {
      // Fallback: open dialog and fetch full text
      handleOpenFullText();
    }
  };

  const handleOpenFullText = async () => {
    setIsDialogOpen(true);
    // Full text viewing is no longer supported for court rulings
    setFullText(t('components.message.fullTextNotAvailable', 'Full text viewing is not available for this document.'));
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleDownload = async (format: 'word' | 'pdf') => {
    setIsDownloading(true);
    try {
      // Proxy the document through our backend (backend will fetch from GCS or scraper URL)
      const proxyResponse = (await proxyDocumentUrl({ documentId }).unwrap())?.result;

      if (!proxyResponse.success || !proxyResponse.content) {
        throw new Error(proxyResponse.error || t('errors.document.failedToFetch', 'Failed to fetch document content'));
      }

      // Decode base64 content if encoded (for UTF-8 preservation)
      let content = proxyResponse.content;
      if (proxyResponse.encoding === 'base64') {
        try {
          // Decode base64 to get proper UTF-8 string
          const binaryString = atob(content);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          const decoder = new TextDecoder('utf-8');
          content = decoder.decode(bytes);
        } catch (error) {
          console.error('Failed to decode base64 content:', error);
          // If decoding fails, use content as-is
        }
      }

      const contentType = proxyResponse.contentType || 'text/html';
      const source = proxyResponse.source || 'unknown';
      const isSourcePDF = contentType.includes('application/pdf');

      console.log('[Download] Source:', source, 'ContentType:', contentType, 'Format requested:', format);

      // Handle different source types
      if (isSourcePDF) {
        // Source is a binary PDF (from scraper URL fallback)
        if (format === 'pdf') {
          // Direct download of the PDF (content is still base64 for PDFs)
          await downloadPDFDirect(proxyResponse.content, documentTitle);
        } else {
          // PDF to Word conversion not supported - backend should have returned text from GCS
          throw new Error(t('components.message.wordNotAvailableUsePDF', 'Word format is not available for this document. Please download as PDF instead.'));
        }
      } else {
        // Source is text/HTML (from GCS or HTML scraper URL)
        // Backend already fetched the best available text (gcsPathMarkdown, gcsPath, or HTML)
        if (format === 'pdf') {
          await downloadAsPDF(content, documentTitle);
        } else {
          await downloadAsWord(content, documentTitle);
        }
      }
    } catch (error: any) {
      console.error('Failed to download document:', error);
      alert(error.message || t('components.message.errorDownloadingDocument', 'Failed to download document. Please try again.'));
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadPDFDirect = async (base64Content: string, filename: string) => {
    // Convert base64 to blob and download
    const byteCharacters = atob(base64Content);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Helper to strip images and external resources from HTML
  const stripImagesFromHtml = (html: string): string => {
    // Remove img tags
    let cleaned = html.replace(/<img[^>]*>/gi, '');

    // Remove background-image styles
    cleaned = cleaned.replace(/background-image\s*:\s*url\([^)]*\)\s*;?/gi, '');

    // Remove other problematic external resources
    cleaned = cleaned.replace(/<link[^>]*>/gi, '');
    cleaned = cleaned.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');

    return cleaned;
  };

  // Simplify HTML for PDF conversion
  const simplifyHtmlForPdf = (html: string): string => {
    let simplified = html;

    // Remove all <style> blocks
    simplified = simplified.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');

    // Remove all style attributes
    simplified = simplified.replace(/\s+style\s*=\s*["'][^"']*["']/gi, '');

    // Remove class and id attributes
    simplified = simplified.replace(/\s+class\s*=\s*["'][^"']*["']/gi, '');
    simplified = simplified.replace(/\s+id\s*=\s*["'][^"']*["']/gi, '');

    // Remove divs and spans (keep their content)
    simplified = simplified.replace(/<\/?div[^>]*>/gi, '');
    simplified = simplified.replace(/<\/?span[^>]*>/gi, '');

    // Remove empty tags
    simplified = simplified.replace(/<(\w+)[^>]*>\s*<\/\1>/gi, '');

    // Normalize whitespace and line breaks
    simplified = simplified.replace(/\s+/g, ' ');
    simplified = simplified.replace(/<br\s*\/?>/gi, '\n');

    // Extract body content
    const bodyContent = simplified.match(/<body[^>]*>([\s\S]*)<\/body>/i)?.[1] || simplified;

    // Clean the content
    const cleaned = bodyContent
      .trim()
      .replace(/\n\s*\n/g, '\n')
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .map(line => {
        if (line.match(/^<h[1-6]/i) || line.match(/^<p>/i) || line.match(/^<table/i) || line.match(/^<ul/i) || line.match(/^<ol/i)) {
          return line;
        }
        return `<p>${line}</p>`;
      })
      .join('\n');

    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'DejaVu Sans', Arial, sans-serif;
      font-size: 11pt;
      line-height: 1.5;
      padding: 15px;
      color: #000;
      background: #fff;
    }
    p { margin: 0.3em 0; }
    h1 { font-size: 16pt; margin: 1em 0 0.5em 0; font-weight: bold; }
    h2 { font-size: 14pt; margin: 0.8em 0 0.4em 0; font-weight: bold; }
    h3 { font-size: 12pt; margin: 0.6em 0 0.3em 0; font-weight: bold; }
    table { border-collapse: collapse; width: 100%; margin: 0.5em 0; }
    td, th { border: 1px solid #333; padding: 4px; text-align: left; font-size: 10pt; }
    ul, ol { margin: 0.3em 0 0.3em 1.5em; }
    li { margin: 0.2em 0; }
  </style>
</head>
<body>
${cleaned}
</body>
</html>`;
  };

  const downloadAsPDF = async (htmlContent: string, filename: string) => {
    const html2pdf = (await import('html2pdf.js')).default;

    // Strip images and simplify HTML for better PDF rendering
    let cleanedContent = stripImagesFromHtml(htmlContent);
    cleanedContent = simplifyHtmlForPdf(cleanedContent);

    const opt = {
      margin: 0.5,
      filename: `${filename}.pdf`,
      image: { type: 'jpeg' as const, quality: 0.95 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        imageTimeout: 0,
        removeContainer: true,
        letterRendering: true
      },
      jsPDF: {
        unit: 'in',
        format: 'letter',
        orientation: 'portrait' as const,
        compress: true
      },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
    };

    html2pdf().set(opt).from(cleanedContent).save();
  };

  // Helper to wrap HTML with proper UTF-8 encoding for Word conversion
  const wrapHtmlForWordConversion = (html: string): string => {
    // Check if HTML already has proper structure
    if (html.toLowerCase().includes('<!doctype') || html.toLowerCase().includes('<html')) {
      // Already has HTML structure, just ensure UTF-8 meta tag
      if (!html.toLowerCase().includes('charset=utf-8')) {
        html = html.replace(
          /<head>/i,
          '<head><meta charset="UTF-8"><meta http-equiv="Content-Type" content="text/html; charset=UTF-8">'
        );
      }
      return html;
    }

    // Wrap content in proper HTML structure with UTF-8 encoding
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; }
    p { margin: 1em 0; }
  </style>
</head>
<body>
${html}
</body>
</html>`;
  };

  const downloadAsWord = async (htmlContent: string, filename: string) => {
    const { asBlob } = await import('html-docx-js-typescript');

    try {
      // Wrap HTML with proper UTF-8 encoding
      const wrappedContent = wrapHtmlForWordConversion(htmlContent);
      const result = await asBlob(wrappedContent);
      let blob: Blob;

      if (result instanceof Blob) {
        blob = result;
      } else if (result && typeof result === 'object' && 'buffer' in result) {
        // Handle Buffer type from Node.js environment
        const arrayBuffer = new Uint8Array(result.buffer as ArrayBuffer);
        blob = new Blob([arrayBuffer], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      } else {
        blob = new Blob([result as BlobPart], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error converting to Word:', error);
      throw error;
    }
  };

  // Get main title display
  const getMainTitle = (): string => {
    if (isUserUpload) {
      return documentTitle;
    }

    if (knowledgebaseName && jurisdiction) {
      return `${knowledgebaseName} (${jurisdiction})`;
    }

    return knowledgebaseName || documentTitle || t('components.message.legalDocument');
  };

  // Get subtitle display
  const getSubtitle = (): string => {
    if (isUserUpload) {
      return t('components.message.userUploadedDocument', 'User Uploaded Document');
    }

    return documentSubtitle || documentTitle || t('components.message.untitledDocument');
  };

  // Get full display title for dialog
  const getDialogTitle = (): string => {
    const mainTitle = getMainTitle();
    const subtitle = getSubtitle();
    return `${mainTitle} - ${subtitle}`;
  };

  return (
    <>
      {/* Clickable Document Card */}
      <div
        onClick={handleCardClick}
        className={cn(
          "relative p-4 bg-background/60 border border-border/20 rounded-lg cursor-pointer transition-all duration-200",
          "hover:bg-gold/5 hover:border-gold/30 hover:shadow-lg hover:-translate-y-0.5"
        )}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {/* Download Button - positioned based on text direction */}
        <div
          className={cn(
            "absolute top-2",
            isRTL ? "left-2" : "right-2"
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 hover:bg-gold/10"
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={isRTL ? "start" : "end"}>
              <DropdownMenuItem onClick={() => handleDownload('pdf')} disabled={isDownloading}>
                <FileDown className="mr-2 h-4 w-4" />
                {t('components.message.downloadAsPDF', 'Download as PDF')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleDownload('word')} disabled={isDownloading}>
                <FileDown className="mr-2 h-4 w-4" />
                {t('components.message.downloadAsWord', 'Download as Word')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="w-full overflow-hidden">
          {/* Main Title */}
          <h3 className="font-bold text-[0.95rem] text-foreground leading-tight mb-1">
            {getMainTitle()}
          </h3>

          {/* Subtitle */}
          <p className="font-medium text-[0.85rem] text-muted-foreground leading-snug mb-2 line-clamp-2">
            {getSubtitle()}
          </p>

          {/* Metadata Badges */}
          <div className="flex gap-2 flex-wrap mb-3">
            <Badge
              variant={getRelevanceColor(relevance)}
              className="text-[0.7rem] h-[22px] font-medium"
            >
              {parseRelevance(relevance, t)}
            </Badge>
            <Badge
              variant="outline"
              className="text-[0.7rem] h-[22px] border-muted-foreground/30 text-muted-foreground"
            >
              {parseContentType(contentType, t)}
            </Badge>
          </div>

          {/* Enhanced Metadata Section */}
          {(confidence !== undefined || showScore || keyTerms?.length || reasoning) && (
            <div className="mt-3 pt-3 border-t border-border/10">
              {/* Confidence Score */}
              {confidence !== undefined && (
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-semibold text-[0.7rem]">
                    {t('confidence')}:
                  </span>
                  <Badge
                    variant={confidence > 0.8 ? 'default' : confidence > 0.6 ? 'secondary' : 'outline'}
                    className="h-[20px] text-[0.65rem]"
                  >
                    {(confidence * 100).toFixed(0)}%
                  </Badge>
                </div>
              )}

              {/* Score Display */}
              {showScore && (
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-semibold text-[0.7rem]">
                    {t('components.documentSearch.score', 'Score')}:
                  </span>
                  <Badge
                    variant="outline"
                    className="h-[20px] text-[0.65rem] border-gold/30"
                  >
                    {score.toFixed(4)}
                  </Badge>
                </div>
              )}

              {/* Key Terms */}
              {keyTerms && keyTerms.length > 0 && (
                <div className="mb-2">
                  <span className="font-semibold text-[0.7rem] block mb-1">
                    {t('keyTerms')}:
                  </span>
                  <div className="flex gap-1 flex-wrap">
                    {keyTerms.slice(0, 4).map((term, idx) => (
                      <Badge
                        key={idx}
                        variant="outline"
                        className="h-[20px] text-[0.65rem] border-gold/30"
                      >
                        {term}
                      </Badge>
                    ))}
                    {keyTerms.length > 4 && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-[0.65rem] text-muted-foreground self-center cursor-help">
                              +{keyTerms.length - 4} {t('more')}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className="flex flex-col gap-2 py-1">
                              {keyTerms.slice(4).map((term, idx) => (
                                <Badge
                                  key={idx}
                                  variant="outline"
                                  className="h-[20px] text-[0.65rem] border-gold/30"
                                >
                                  {term}
                                </Badge>
                              ))}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                </div>
              )}

              {/* Reasoning - Expandable */}
              {reasoning && (
                <div>
                  <span className="font-semibold text-[0.7rem] block mb-1">
                    {t('aiReasoning')}:
                  </span>
                  <p className="text-[0.7rem] text-muted-foreground line-clamp-[10] leading-relaxed">
                    {reasoning}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Action indicator - shows what will happen when clicked */}
          <div className="mt-3 flex items-center gap-1">
            {documentUrl ? (
              <>
                <ExternalLink className="h-3 w-3 text-gold" />
                <span className="text-[0.75rem] font-medium text-gold">
                  {t('components.message.clickToOpenDocument')}
                </span>
              </>
            ) : (
              <>
                <FileText className="h-3 w-3 text-muted-foreground" />
                <span className="text-[0.75rem] font-medium text-muted-foreground">
                  {t('components.message.clickToViewText')}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Full Text Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between pr-8">
              <DialogTitle className="text-base font-semibold">
                {getDialogTitle()}
              </DialogTitle>
            </div>
          </DialogHeader>

          <div className="py-4">
            {fullText ? (
              <div className="p-4">
                {fullText.startsWith('Error:') ? (
                  <p className="text-sm text-destructive">
                    {fullText}
                  </p>
                ) : (
                  formatLegalText(fullText)
                )}
              </div>
            ) : (
              <div className="py-8 text-center text-muted-foreground">
                <p className="text-sm">
                  {t('components.message.noFullTextAvailable')}
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">
                {t('common.close', 'Close')}
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RelevantDocumentCard;
